import { API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import { Box, Button, Dialog, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExoForm from './ExoForm';
import * as Yup from 'yup';
import Loader from '../special/Loader';
import { useEffect, useRef, useState } from 'react';
import SuccessCheck from '../special/SuccessCheck';
import FailureCross from '../special/FailureCross';
import ExoDialog from './ExoDialog';
import ExoDocumentPreview from './ExoDocumentPreview';
import ExoAlert from './ExoAlert';
import { set } from 'date-fns';

const ExoDocumentFromDraft = ({
  dataCollection,
  recipient,
  type,
  id,
  label,
  open,
  onClose,
  allowCC,
  askConfirmation,
  previewDocument,
  handleUpdate,
}) => {
  const config = useConfig();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [successfull, setSuccessfull] = useState(null);
  const [message, setMessage] = useState('');

  // store delivery object from ExoForm
  const [deliveryData, setDeliveryData] = useState(null);

  // ExoDocumentPreview dialog
  const [openDialogPreview, setOpenDialogPreview] = useState(false);

  // Confirmation alert for creating Document from Draft
  const [confirmInvoiceAlert, setConfirmInvoiceAlert] = useState(null);

  // Automatically close the dialog after 3 seconds if the (draft -> document) operation was successful
  useEffect(() => {
    if (successfull) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);

      return () => clearTimeout(timer); // cleanup if component unmounts or state changes
    }
  }, [successfull, onClose]);

  useEffect(() => {
    setSuccessfull(null);
  }, [id, type]);

  function handleSendSuccess(data) {
    setSuccessfull(true);
    setMessage(data.message);
  }

  function handleSendFailed(data) {
    setSuccessfull(false);
    setMessage(data.message);
  }

  //close document preview dialog
  function handleCancel() {
    setOpenDialogPreview(false);
  }

  function confirmSubmit() {
    if (askConfirmation) {
      setConfirmInvoiceAlert({
        type: 'warning',
        message: t(
          'You are about to create a legally binding document. Are you sure?',
        ),
        submitLabel: t('Yes'),
        cancelLabel: t('No'),
        onSubmit: () => {
          setConfirmInvoiceAlert(null);
          handleSubmit();
        },
        onCancel: () => setConfirmInvoiceAlert(null),
      });
    } else {
      handleSubmit();
    }
  }

  function handleSubmitData(data) {
    setDeliveryData(data.delivery);
    setOpenDialogPreview(true);
  }

  function handleSubmit() {
    setOpenDialogPreview(false);

    // add delivery data and isDraft= false to dataCollection and send API request to create Document from Draft
    const updatedDataCollection = {
      ...dataCollection,
      isDraft: false,
      delivery: deliveryData,
    };

    delete updatedDataCollection['sourceType'];

    DBRequest({
      config,
      path: `${type}/${dataCollection.id}`,
      method: API_PATCH(updatedDataCollection),
      onResponse: (res) => {
        handleSendSuccess(res);
        handleUpdate(res);
        setOpenDialogPreview(false);
      },
      onLoading: setIsLoading,
      onError: handleSendFailed,
    });
  }

  // A recipient without an e-mail address leaves `deliveryMail: null` in the
  // delivery object, and "Save Only" keeps it there. `nullable()` lets that
  // null through; `required()` still rejects it for every mail delivery.
  const validationSchema = Yup.object().shape({
    delivery: Yup.object().shape({
      deliveryMail: Yup.string()
        .nullable()
        .email(t('Invalid email format'))
        .when('saveOnly', {
          is: true,
          otherwise: (schema) =>
            schema.required(t('Delivery mail id is required')),
        }),
      deliveryName: Yup.string()
        .nullable()
        .when('saveOnly', {
          is: true,
          otherwise: (schema) =>
            schema.required(t('Delivery name id is required')),
        }),
    }),
  });

  return (
    <>
      <ExoDialog open={open} limitWidth>
        <Loader active={isLoading} />
        {successfull !== null ? (
          <Box className="flex h-full flex-col">
            <Box className="flex h-full justify-center items-center flex-col">
              {successfull ? <SuccessCheck /> : <FailureCross />}
              <Typography variant="h4" textAlign="center">
                {message}
              </Typography>
            </Box>
            <Box className="flex flex-row-reverse justify-between items-center">
              {!successfull && (
                <Button
                  onClick={() => setSuccessfull(null)}
                  variant="contained"
                  color={'warning'}
                >
                  {t('Retry')}
                </Button>
              )}
              <Button onClick={onClose} color={'error'} variant="outlined">
                {t('Close')}
              </Button>
            </Box>
          </Box>
        ) : (
          <ExoForm
            className="flex flex-col justify-between overflow-hidden"
            fields={[
              {
                key: 'delivery',
                type: 'delivery',
                label: t('Delivery'),
                options: [
                  'recipientMail',
                  'contactMail',
                  'customMail',
                  'saveOnly',
                ],
                allowCC: allowCC,
              },
            ]}
            startDataCollection={{ recipient: recipient }}
            onSubmit={handleSubmitData}
            onCancel={onClose}
            validationSchema={validationSchema}
          />
        )}
      </ExoDialog>
      <PreviewBeforeSend
        onCancel={handleCancel}
        onSubmit={confirmSubmit}
        allowDraft={false}
        open={openDialogPreview}
        dataCollection={dataCollection}
        previewDocument={previewDocument}
      />
      <ExoAlert
        open={Boolean(confirmInvoiceAlert)}
        alert={confirmInvoiceAlert || {}}
        onClose={confirmInvoiceAlert?.onCancel}
        onSubmit={confirmInvoiceAlert?.onSubmit}
      />
    </>
  );
};

const PreviewBeforeSend = ({
  dataCollection = {},
  previewDocument,
  open,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const dialogContainerRef = useRef(null);
  const [maxDialogContainerHeight, setMaxDialogContainerHeight] = useState(0);

  useEffect(() => {
    if (!dialogContainerRef) return;

    // Attach event listener for window resize
    window.addEventListener('resize', handleResize);

    // Initial setup
    handleResize();

    // Cleanup the event listener when the component is unmounted
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [dialogContainerRef, dialogContainerRef.current]);

  function handleResize() {
    if (dialogContainerRef.current) {
      const maxDialogHeight =
        dialogContainerRef.current.offsetWidth * Math.sqrt(2);
      setMaxDialogContainerHeight(maxDialogHeight);
    }
  }

  return (
    <Dialog
      open={open}
      sx={{
        '& .MuiPaper-root': {
          width: 'auto!important',
          backgroundColor: 'transparent',
        },
      }}
    >
      <Box
        ref={dialogContainerRef}
        className="flex overflow-hidden"
        sx={{
          width: 'calc((90vh - 49px) / 1.414)',
          height: '(90vh - 49px)',
          maxWidth: '100%',
          maxHeight: maxDialogContainerHeight
            ? `${maxDialogContainerHeight}px`
            : 'auto',
          aspectRatio: '0.707!important',
        }}
      >
        <ExoDocumentPreview
          dataCollection={dataCollection}
          documentId={dataCollection.templateId}
          previewDocument={previewDocument}
          //   attachments={dataCollection.attachments || []}
          //   immediate={true}
        />
      </Box>
      <Box className="p-2 flex flex-row w-full justify-between bottom-0 left-0">
        <Button onClick={onCancel}>{t('Back')}</Button>

        <Box className="flex gap-2">
          <Button onClick={onSubmit} color={'success'} variant="contained">
            {dataCollection.delivery && dataCollection.delivery.saveOnly
              ? t('Save')
              : t('Send')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ExoDocumentFromDraft;
